import { GetState, SetState, StateCreator, StoreApi, UseStore } from 'zustand';
import { Segment } from '@hotjar/global-filters/src/domains/global-filters/model/segment';
import type { Recording as OriginalRecording } from '@hotjar/webapp/features/Playbacks/types';
import type { Recording as PlaybackRecording } from '@hotjar/webapp/domains/recordings/types';
import type { Summary } from '@hotjar/insights-common/lib/targeting-rules';
import type { Site } from '@hotjar/site';
import type {
  QueryFormatFilter,
  QueryMapFilter,
  AddFilterTippyInstance,
} from '@hotjar/global-filters';
import { CommentsByRecordingId, LabelsByRecordingId } from '../../domains/highlights';

// @TODO To avoid circular dependencies we are duplicating this type.
// At the moment this type it's used in two packages.
// We can tolerate duplication at this point but ideally should be moved in the common package.
// packages/legacy/src/webapp/domains/activation/tracking-code/types.ts
interface LastData {
  hoursSince: number | null;
  timestamp: number | null;
}

// @TODO To avoid circular dependencies we are duplicating this type.
// At the moment this type it's used in two packages.
// We can tolerate duplication at this point but ideally should be moved in the common package.
// packages/legacy/src/webapp/domains/activation/tracking-code/types.ts
interface TrackingCodeState {
  installed: boolean;
  isActive: boolean;
  lastData: LastData;
}

export interface Recording extends OriginalRecording {
  selected: boolean;
}

export type SelectedRecording = {
  id: Recording['id'];
  created: Recording['created_epoch_time'];
};

export const HotjarFilterOptionNotWatched = 'not_watched';

export const HotjarFilterOptionWatched = 'watched';

export const HotjarFilterOptionAll = 'all';

export type WatchedHotjarFilter =
  | typeof HotjarFilterOptionNotWatched
  | typeof HotjarFilterOptionWatched
  | typeof HotjarFilterOptionAll;

export type HotjarFilters = {
  watched: WatchedHotjarFilter;
  relevanceScore: string[];
};

export type JsonFilters = QueryFormatFilter;

export type TippyInstance = AddFilterTippyInstance;

type RecordingSettings = {
  excludeMinimalActivity: boolean | null;
  excludeShortRecordings: boolean | null;
  recordingCaptureKeystrokes: boolean | null;
  recordingTargeting: Summary;
};

type OverviewData = {
  recordingsRelevant: PlaybackRecording[];
  recordingsWithRageClicks: PlaybackRecording[];
  recordingsEngaged: PlaybackRecording[];
  recordingsWithUturns: PlaybackRecording[];
  recordingsWithErrors: PlaybackRecording[];
  recordingsFrustrated: PlaybackRecording[];
  recordingsRelevantTotalHits: number;
  recordingsWithRageClicksTotalHits: number;
  recordingsEngagedTotalHits: number;
  recordingsWithUturnsTotalHits: number;
  recordingsWithErrorsTotalHits: number;
  recordingsFrustratedTotalHits: number;
};

export type Column = {
  label: string;

  key: string;
  show: boolean;
  showInPanel: boolean;
  isDisabled?: boolean;
};

export type ColumnKey =
  | 'comments'
  | 'relevance'
  | 'date'
  | 'user'
  | 'country'
  | 'action_count'
  | 'page_count'
  | 'duration'
  | 'device'
  | 'browser'
  | 'operating_system'
  | 'referrer_url'
  | 'landing_url'
  | 'exit_url'
  | 'frustration'
  | 'engagement';

export type Columns = Record<ColumnKey, Column>;

export type BatchedMiddleware = (
  config: StateCreator<State>,
) => (set: SetState<State>, get: GetState<State>, api: StoreApi<State>) => State;

export type State = {
  // Scope Set-Able
  addFilterTippyInstance: TippyInstance | null;
  automatedFilterUpdateFlag: boolean;

  // Scope setters
  setAutomatedFilterUpdateFlag: (flag: boolean) => void;
  setAddFilterTippyInstance: (tippyInstance: TippyInstance | null) => void;

  // Model
  commentsByRecordingId: CommentsByRecordingId;
  columns: Columns;
  currentPage: number;
  currentSegment: Segment | null;
  editedSegment: Segment | null;
  effectiveDateRangeFilter: QueryMapFilter | null;
  exportBannerKind: string | null;
  exportBannerMessage: string | null;
  hotjarFilters: HotjarFilters;
  isOverviewExperimentEnabled: boolean;
  itemsPerPage: number;
  jsonFilters: JsonFilters | null;
  labelsByRecordingId: LabelsByRecordingId;
  loaded: boolean;
  previousPage: number;
  recordings: Recording[];
  recordingsRequestTimeRange: number[] | null;
  recordingMode: string | null;
  recordingSettings: RecordingSettings;
  recordingsFetchedWithV3Api: boolean;
  searching: boolean;
  selectedAllRecordings: boolean;
  selectedRecordings: SelectedRecording[];
  showExportBanner: boolean;
  showNotWatchedPopoverInfo: boolean;
  site: Site | null;
  segments: Segment[];
  sortBy: string | null;
  sortInitialOption: string;
  sortInitialReverse: boolean;
  totalHits: number;
  totalRecordingsCount: number;
  trackingCode: TrackingCodeState | null;
  trackPageChange: boolean;
  // Overview
  overviewData: OverviewData;
  widgetsLoaded: boolean;

  // Model setters
  setColumns: (columns: Columns) => void;
  setCurrentPage: (currentPage: number) => void;
  setCurrentSegment: (currentSegment: Segment | null) => void;
  setCommentsByRecordingId: (commentsByRecordingId: CommentsByRecordingId) => void;
  setEditedSegment: (editedSegment: Segment | null) => void;
  setEffectiveDateRangeFilter: (effectiveDateRangeFilter: QueryMapFilter) => void;
  setExportBannerKind: (kind: string) => void;
  setExportBannerMessage: (message: string) => void;
  setHotjarFilters: (hotjarFilters: HotjarFilters) => void;
  setIsOverviewExperimentEnabled: (isOverviewExperimentEnabled: boolean) => void;
  setItemsPerPage: (itemsPerPage: number) => void;
  setJsonFilters: (jsonFilters: JsonFilters) => void;
  setLabelsByRecordingId: (labelsByRecordingId: LabelsByRecordingId) => void;
  setLoaded: (loaded: boolean) => void;
  setPreviousPage: (previousPage: number) => void;
  setRecordings: (recordings: Recording[]) => void;
  setRecordingsRequestTimeRange: (recordingsRequestTimeRange: [number, number]) => void;
  setRecordingMode: (recordingMode: string) => void;
  setRecordingSettings: (recordingSettings: RecordingSettings) => void;
  setRecordingsFetchedWithV3Api: (recordingsFetchedWithV3Api: boolean) => void;
  setSearching: (searching: boolean) => void;
  setSelectedAllRecordings: (selectedAllRecordings: boolean) => void;
  setSelectedRecordings: (selectedRecordings: SelectedRecording[]) => void;
  setShowExportBanner: (showExportBanner: boolean) => void;
  setShowNotWatchedPopoverInfo: (showNotWatchedPopoverInfo: boolean) => void;
  setSite: (site: Site) => void;
  setSegments: (segments: Segment[]) => void;
  setSortBy: (sortBy: string) => void;
  setSortInitialOption: (sortInitialOption: string) => void;
  setSortInitialReverse: (sortInitialReverse: boolean) => void;
  setTrackPageChange: (trackPageChange: boolean) => void;
  setTotalHits: (totalHits: number) => void;
  setTotalRecordingsCount: (totalRecordingCount: number) => void;
  setTrackingCode: (trackingCode: TrackingCodeState) => void;
  // Overview
  setOverviewData: (overviewData: OverviewData) => void;
  setWidgetsLoaded: (widgetsLoaded: boolean) => void;

  // getters
  isTrackingCodePromptVisible: () => boolean;
  isEmptyStateCardVisible: () => boolean;
  inTeaserMode: () => boolean;
  showActions: () => boolean;

  // async actions
  retrieveHighlightsCommentsAndLabels: () => Promise<void>;
  retrieveTotalRecordingsCount: () => Promise<number>;
};

export type Store = UseStore<State>;
